import { useState } from "react";
import CVPreview from "./CVPreview";
import {
  Award,
  Sparkles,
  Target,
  BookOpen,
  CheckCircle2,
  Layers,
} from "lucide-react";

// Template configs matching the backend seed (name, engine, colour, description)
export const TEMPLATES = [
  {
    id: "classic",
    label: "Classic Bloom",
    tagline: "Timeless. ATS-friendly. Works for every industry.",
    engine: "classic",
    primary_color: "#F39319",
    icon: Award,
  },
  {
    id: "modern",
    label: "Modern Charcoal",
    tagline: "Bold header band. Confident. Senior-friendly.",
    engine: "modern",
    primary_color: "#0f172a",
    icon: Layers,
  },
  {
    id: "compact",
    label: "Compact One-Pager",
    tagline: "Dense one-page layout. Graduate & mid-level roles.",
    engine: "compact",
    primary_color: "#0e7c66",
    icon: Target,
  },
];

// Sample CVs — each written using professional CV-writing methodology:
// strong action verbs, quantified impact, STAR bullet structure, ATS-safe
// sections and recruiter-focused positioning.
export const SAMPLES = {
  classic: {
    // Senior Marketing Manager — Classic serif-style layout
    personal: {
      full_name: "Sarah Reyes",
      email: "sarah.reyes@example.com",
      phone: "+44 7700 900123",
      location: "London, UK",
      linkedin: "linkedin.com/in/sarahreyes",
    },
    professional_summary:
      "B2B marketing leader with 9+ years scaling SaaS demand-generation engines for FTSE-listed and Series-B tech businesses. Recognised for translating commercial goals into multi-channel campaigns that deliver measurable pipeline growth, brand equity and marketing-sourced revenue.",
    core_skills: [
      "Demand Generation",
      "ABM Strategy",
      "Marketing Automation (HubSpot, Marketo)",
      "SEO & Content Strategy",
      "Team Leadership (10+ reports)",
      "P&L Ownership",
      "Analytics & Attribution",
      "Brand Positioning",
    ],
    experience: [
      {
        title: "Senior Marketing Manager",
        company: "Northwind SaaS Ltd",
        location: "London",
        start_date: "Mar 2022",
        end_date: "Present",
        bullets: [
          "Grew marketing-sourced pipeline by 168% (£4.6M → £12.3M) in 18 months by launching an integrated ABM programme across LinkedIn, email and paid search.",
          "Rebuilt the demand-gen funnel — cut cost-per-MQL from £142 to £78 while lifting SQL conversion from 9% to 22%.",
          "Led a team of 6 marketers plus 3 agencies; introduced OKRs and quarterly business reviews adopted org-wide.",
        ],
      },
      {
        title: "Marketing Manager",
        company: "Beacon Analytics",
        location: "London",
        start_date: "Jun 2019",
        end_date: "Feb 2022",
        bullets: [
          "Launched the company's first content programme — 42 assets in year one — driving 61% of inbound trial signups by close of FY21.",
          "Delivered a full brand refresh (logo, positioning, tone) landing on time and under a £120K budget; measured brand recall lift of +14pts.",
        ],
      },
    ],
    education: [
      {
        degree: "BA (Hons) Business & Marketing",
        institution: "University of Manchester",
        start_date: "2012",
        end_date: "2015",
      },
    ],
    certifications: [
      "CIM Level 6 Diploma in Professional Marketing",
      "Google Analytics 4 — Certified",
    ],
    languages: ["English (native)", "Spanish (professional working)"],
    achievements: [
      "B2B Marketing Awards 2024 — Winner, Best ABM Campaign (168% pipeline growth)",
      "Promoted twice in three years — fastest route to Senior Manager in company history",
      "Speaker, SaaStock London 2023 & MarTech Summit 2024",
      "Built and retained a 10-person team with zero regretted attrition over 2 years",
    ],
  },
  modern: {
    // Senior Software Engineer — Modern coloured-header layout
    personal: {
      full_name: "James Patel",
      email: "james.patel@example.com",
      phone: "+44 7700 900456",
      location: "Manchester, UK",
      linkedin: "linkedin.com/in/jamespatel",
    },
    professional_summary:
      "Backend-leaning full-stack engineer with 7 years shipping high-availability services at scale. Expert in Go, Python and event-driven systems on AWS. Trusted to lead architecture reviews, mentor mid-level engineers and drive tricky migrations without downtime.",
    core_skills: [
      "Go · Python · TypeScript",
      "AWS (ECS, Lambda, DynamoDB, Kinesis)",
      "Event-Driven Architecture",
      "Kubernetes · Terraform",
      "PostgreSQL · Redis",
      "Distributed Systems",
      "CI/CD (GitHub Actions, ArgoCD)",
      "Observability (Datadog, Prometheus)",
    ],
    experience: [
      {
        title: "Senior Software Engineer",
        company: "Kestrel Payments",
        location: "Manchester (Hybrid)",
        start_date: "Jan 2023",
        end_date: "Present",
        bullets: [
          "Re-architected the settlement pipeline from a monolithic cron job to an event-driven Kinesis + Lambda pipeline — cut end-to-end latency from 42 min to 90 sec at 3× the volume.",
          "Led a zero-downtime PostgreSQL 11 → 15 migration across 6 services and 2.4 TB of data; wrote the internal migration playbook now used company-wide.",
          "Mentored 4 mid-level engineers; two promoted to senior within 9 months.",
        ],
      },
      {
        title: "Software Engineer",
        company: "Loop Retail",
        location: "London",
        start_date: "Aug 2020",
        end_date: "Dec 2022",
        bullets: [
          "Built the pricing engine handling 8M SKUs and 400 rps at p99 under 45 ms — still in production, unchanged, three years on.",
          "Introduced feature flagging via LaunchDarkly across the platform, unblocking 6 product teams to ship independently.",
        ],
      },
      {
        title: "Graduate Software Engineer",
        company: "OpenBank UK",
        location: "London",
        start_date: "Sep 2018",
        end_date: "Jul 2020",
        bullets: [
          "Delivered the customer-facing rewards API used by 900K app users; ranked top-5 on internal engineering scorecard.",
        ],
      },
    ],
    education: [
      {
        degree: "MEng Computer Science, 1st Class",
        institution: "University of Bristol",
        start_date: "2014",
        end_date: "2018",
      },
    ],
    certifications: [
      "AWS Certified Solutions Architect — Professional",
      "Certified Kubernetes Administrator (CKA)",
    ],
    achievements: [
      "Cut cloud spend by £340K/year through service consolidation and right-sizing — recognised with the company Impact Award 2024",
      "Speaker, GoUK 2024 — 'Idempotent payments at scale' (450+ attendees)",
      "Open source: maintainer of go-retryable — 2.1K GitHub stars, used by 3 FTSE 100 engineering teams",
      "On-call incident lead — drove MTTR down from 74 min to 18 min across the payments platform",
    ],
  },
  compact: {
    // Graduate Business Analyst — Compact one-pager for early careers
    personal: {
      full_name: "Ellie Brooks",
      email: "ellie.brooks@example.com",
      phone: "+44 7700 900789",
      location: "Bristol, UK",
      linkedin: "linkedin.com/in/elliebrooks",
    },
    professional_summary:
      "Data-driven graduate business analyst combining a 1st-class Economics degree with 15 months of hands-on placement experience at a top-4 UK bank. Skilled in SQL, Tableau and structured business analysis frameworks — ready to add value from day one.",
    core_skills: [
      "SQL · Excel (Advanced) · Tableau",
      "Business Requirements Analysis",
      "Process Mapping (BPMN)",
      "Stakeholder Interviews",
      "A/B Testing",
      "Agile / Scrum",
      "Python (Pandas basics)",
      "Presentation & Storytelling",
    ],
    experience: [
      {
        title: "Business Analyst Placement",
        company: "Lloyds Banking Group",
        location: "Bristol",
        start_date: "Jul 2023",
        end_date: "Aug 2024",
        bullets: [
          "Automated a monthly complaints report using SQL + Tableau — saved 12 hours per week of analyst time and became the standard across three teams.",
          "Ran 14 stakeholder interviews to shape the requirements for a new customer feedback tool; solution went live 3 weeks ahead of schedule.",
          "Presented data insights to a Senior Director monthly; recommendation to reprioritise a £280K product feature was adopted.",
        ],
      },
      {
        title: "Data Analyst Intern",
        company: "Sage",
        location: "Newcastle",
        start_date: "Jun 2022",
        end_date: "Sep 2022",
        bullets: [
          "Built a churn prediction dashboard using SQL and Tableau — identified 8% of the customer base at risk, informing a retention campaign that recovered £96K ARR.",
        ],
      },
    ],
    education: [
      {
        degree: "BSc (Hons) Economics — First Class",
        institution: "University of Bristol",
        start_date: "2020",
        end_date: "2023",
      },
    ],
    certifications: ["Tableau Desktop Specialist", "AWS Cloud Practitioner"],
    achievements: [
      "Winner, Lloyds Placement Awards 2024 — 'Above & Beyond' (1 of 3 from 120 placements)",
      "Top 5% of cohort — Bristol Economics Society, 2023",
      "Vice-President, University Data Science Society — grew membership from 60 to 210",
    ],
  },
};

const CRAFT_NOTES = {
  classic: [
    {
      icon: Target,
      title: "Positioned for the next role",
      text: "Headline + summary target Senior/Head-of, not the role she's in.",
    },
    {
      icon: Sparkles,
      title: "Quantified every bullet",
      text: "£, %, timeframes — recruiter's eyes lock onto numbers first.",
    },
    {
      icon: BookOpen,
      title: "Follows the STAR shape",
      text: "Situation, Task, Action, Result — the industry standard.",
    },
  ],
  modern: [
    {
      icon: Target,
      title: "Domain signals up-front",
      text: "Tech stack in the top 3 lines — matches recruiter keyword scans.",
    },
    {
      icon: Sparkles,
      title: "Impact before responsibility",
      text: "Leads with outcomes (\"cut latency 42 min → 90 sec\"), not duties.",
    },
    {
      icon: BookOpen,
      title: "Senior-friendly formatting",
      text: "Bold coloured header + confident tone — reads as leadership.",
    },
  ],
  compact: [
    {
      icon: Target,
      title: "Graduate CV done right",
      text: "Placement + internship framed like real roles, with measurable output.",
    },
    {
      icon: Sparkles,
      title: "One page, high signal",
      text: "Dense but never cluttered — every line earns its place.",
    },
    {
      icon: BookOpen,
      title: "Follows recruiter book rules",
      text: "Reverse-chronological, verbs first, no first-person pronouns.",
    },
  ],
};

export default function CvSamples() {
  const [tab, setTab] = useState("classic");
  const template = TEMPLATES.find((t) => t.id === tab);
  const cv = SAMPLES[tab];
  const notes = CRAFT_NOTES[tab];

  return (
    <section
      id="cv-samples"
      data-testid="ai-cv-maker-samples"
      className="relative py-20 md:py-28 bg-muted/30 border-y border-border"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <p className="label-eyebrow">View CV Samples</p>
          <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
            Three Templates.{" "}
            <span className="text-brand">One Professional Standard.</span>
          </h2>
          <p className="mt-5 text-foreground/70 leading-relaxed">
            Every template follows professional CV writing methodology
            developed by experienced UK CV writers, combining
            recruiter-informed best practices, ATS optimisation principles and
            UK CV standards.
          </p>
          <p className="mt-3 text-foreground/70 leading-relaxed">
            Choose a template to see it in action.
          </p>
        </div>

        {/* Tabs */}
        <div
          data-testid="cv-samples-tabs"
          role="tablist"
          className="mt-8 inline-flex flex-wrap items-center gap-1 bg-background border border-border rounded-2xl p-1.5"
        >
          {TEMPLATES.map((t) => {
            const selected = tab === t.id;
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={selected}
                data-testid={`cv-sample-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                  selected
                    ? "bg-foreground text-background shadow"
                    : "text-foreground/70 hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-sm text-foreground/60">{template.tagline}</p>

        {/* Sample + craft notes */}
        <div className="mt-10 grid lg:grid-cols-12 gap-8 items-start">
          {/* Craft-notes column */}
          <aside
            data-testid="cv-samples-notes"
            className="lg:col-span-4 lg:sticky lg:top-28 space-y-4"
          >
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand">
                Why this sample works
              </p>
              <ul className="mt-4 space-y-4">
                {notes.map((n) => (
                  <li
                    key={n.title}
                    className="flex items-start gap-3"
                    data-testid={`cv-sample-note-${n.title.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="h-9 w-9 rounded-xl bg-brand/10 text-brand flex items-center justify-center shrink-0">
                      <n.icon className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="font-semibold text-sm leading-snug">
                        {n.title}
                      </p>
                      <p className="text-[13px] text-foreground/70 mt-0.5 leading-relaxed">
                        {n.text}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-brand/25 bg-brand/5 p-5">
              <div className="flex items-center gap-2 text-brand text-xs font-bold uppercase tracking-[0.18em]">
                <CheckCircle2 className="h-4 w-4" /> ATS · Recruiter-approved
              </div>
              <p className="mt-3 text-sm text-foreground/85 leading-relaxed">
                Structure, keywords and formatting all pass Applicant Tracking
                Systems used across the UK.
              </p>
            </div>
          </aside>

          {/* Sample preview column */}
          <div className="lg:col-span-8">
            <div
              data-testid={`cv-sample-preview-${tab}`}
              className="rounded-3xl border border-border bg-card p-3 md:p-4 shadow-xl overflow-hidden"
            >
              <CVPreview cv={cv} template={template} className="!shadow-none !border-0" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
